import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';
import { acomptesAPI } from '../../../services/api';
import { Acompte, AcompteCreate, AcompteUpdate } from '../../../types/acompte';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import PriceInput, { parsePriceValue } from '../../../components/PriceInput';
import {
  SlideUpTransition,
  useMinimizable,
  DialogControls,
  MinimizedBar,
} from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

interface AcompteFormDialogProps {
  open: boolean;
  onClose: () => void;
  acompte: Acompte | null;
  employees: EmployeeDropdownItem[];
  onSuccess: () => void;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const AcompteFormDialog: React.FC<AcompteFormDialogProps> = ({
  open,
  onClose,
  acompte,
  employees,
  onSuccess,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDropdownItem | null>(null);
  const [formData, setFormData] = useState<{
    montant: number | null | string;
    date_demande: string;
    motif: string;
  }>({
    montant: null,
    date_demande: new Date().toISOString().split('T')[0],
    motif: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useAlert();

  // Draft caching — only for new acomptes (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    selectedEmployee: EmployeeDropdownItem | null;
    formData: typeof formData;
  }>('acompte', !acompte);

  useEffect(() => {
    if (open) {
      if (acompte) {
        // Find the employee in the list
        const emp = employees.find(e => e.id === acompte.employee_id) || null;
        setSelectedEmployee(emp);
        setFormData({
          montant: acompte.montant,
          date_demande: acompte.date_demande,
          motif: acompte.motif || '',
        });
      } else {
        // New mode — repopulate from a draft if one was stashed by an accidental
        // backdrop/Escape close, otherwise do the blank reset.
        const draft = loadDraft();
        if (draft) {
          setSelectedEmployee(draft.selectedEmployee);
          setFormData(draft.formData);
        } else {
          setSelectedEmployee(null);
          setFormData({
            montant: null,
            date_demande: new Date().toISOString().split('T')[0],
            motif: '',
          });
        }
      }
      setError(null);
    }
  }, [open, acompte, employees, loadDraft]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handlePriceChange = (value: number | null | string) => {
    setFormData(prev => ({ ...prev, montant: value }));
    setError(null);
  };

  const handleEmployeeChange = (_: any, value: EmployeeDropdownItem | null) => {
    setSelectedEmployee(value);
    setError(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedEmployee) {
      setError('Veuillez selectionner un employe');
      return;
    }
    const montant = parsePriceValue(formData.montant);

    if (montant <= 0) {
      setError('Le montant doit etre superieur a 0');
      return;
    }
    if (!formData.date_demande) {
      setError('La date de demande est requise');
      return;
    }

    setLoading(true);
    try {
      if (acompte) {
        const updateData: AcompteUpdate = {
          montant: montant,
          date_demande: formData.date_demande,
          motif: formData.motif || null,
        };
        await acomptesAPI.update(acompte.id, updateData);
        showSuccess('Demande d\'acompte modifiee avec succes');
      } else {
        const createData: AcompteCreate = {
          employee_id: selectedEmployee.id,
          montant: montant,
          date_demande: formData.date_demande,
          motif: formData.motif || null,
        };
        await acomptesAPI.create(createData);
        showSuccess('Demande d\'acompte creee avec succes');
      }
      clearDraft();
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Annuler = deliberate cancel: discard any draft, then close.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (!acompte) {
        saveDraft({ selectedEmployee, formData });
      }
      onClose();
    }
  };

  return (
    <>
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={SlideUpTransition}
    >
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {acompte ? 'Modifier la Demande d\'Acompte' : 'Nouvelle Demande d\'Acompte'}
          <DialogControls color="white" onMinimize={minimize} onClose={handleClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              options={employees}
              getOptionLabel={(option) => `${option.matricule} - ${option.nom_complet}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!!acompte}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight="500">
                      {option.nom_complet}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.matricule} {option.fonction ? `- ${option.fonction}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employe *"
                  size="small"
                  placeholder="Rechercher par nom ou matricule..."
                />
              )}
              noOptionsText="Aucun employe trouve"
              clearText="Effacer"
              openText="Ouvrir"
              closeText="Fermer"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <PriceInput
              value={formData.montant}
              onChange={handlePriceChange}
              label="Montant *"
              currency="DH"
              required
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Date de Demande *"
              name="date_demande"
              type="date"
              value={formData.date_demande}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Motif / Raison"
              name="motif"
              multiline
              rows={3}
              value={formData.motif}
              onChange={handleChange}
              placeholder="Raison de la demande d'acompte..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
        >
          {loading ? <CircularProgress size={24} /> : (acompte ? 'Modifier' : 'Creer')}
        </Button>
      </DialogActions>
    </Dialog>

    <MinimizedBar
      open={open && minimized}
      color="#009688"
      title={acompte ? 'Modifier la Demande d\'Acompte' : 'Nouvelle Demande d\'Acompte'}
      onRestore={restore}
      onClose={handleClose}
    />
    </>
  );
};

export default AcompteFormDialog;
